import { Component } from '@theme/component';

/**
 * Bundle Pack Selector Component
 * Handles the selection of bundle pack sizes (1 Pack, 3 Pack, 6 Pack)
 * TrueNeeds V1 Design
 */
class BundlePackSelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    this.packRadios = this.querySelectorAll('.tn-bundle-pack-selector__radio');
    this.selectedPackSize = 3; // Default to 3 (3 Pack)

    this.packRadios.forEach((radio) => {
      radio.addEventListener('change', this.handlePackChange.bind(this));
    });

    // Initialize with selected pack
    const selectedRadio = this.querySelector('.tn-bundle-pack-selector__radio:checked');
    if (selectedRadio) {
      this.handlePackChange({ target: selectedRadio });
    }
  }

  handlePackChange(event) {
    const radio = event.target;
    const packCount = parseInt(radio.dataset.packCount, 10);
    const variantId = radio.value;

    // Update visual state
    this.updateSelectedState(radio);

    this.selectedPackSize = packCount;

    // Dispatch event to bundle configurator
    this.dispatchEvent(
      new CustomEvent('pack:selected', {
        bubbles: true,
        detail: {
          packCount: packCount,
          variantId: variantId,
          packSize: radio.value,
        },
      })
    );
  }

  updateSelectedState(selectedRadio) {
    // Remove selected class from all options
    this.querySelectorAll('.tn-bundle-pack-selector__option').forEach((option) => {
      option.classList.remove('tn-bundle-pack-selector__option--selected');
    });

    // Add selected class to current option
    const selectedOption = selectedRadio.closest('.tn-bundle-pack-selector__option');
    if (selectedOption) {
      selectedOption.classList.add('tn-bundle-pack-selector__option--selected');
    }
  }
}

/**
 * Bundle Configurator Component
 * Handles dynamic item configurators based on selected pack size
 * 1 Pack = 1 configurator, 3 Pack = 3 configurators, 6 Pack = 6 configurators
 * TrueNeeds V1 Design
 */
class BundleConfiguratorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    this.container = this.querySelector('[data-configurators-container]');
    this.template = this.querySelector('#pack-configurator-template');
    this.productId = this.dataset.productId;

    // Parse product variants data
    const variantsScript = this.querySelector('[data-product-variants]');
    if (variantsScript) {
      try {
        this.variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        this.variants = [];
      }
    }

    // Listen for pack selection changes
    document.addEventListener('pack:selected', this.handlePackSelected.bind(this));

    // Store variant selections
    this.selections = new Map();

    // Attach event listeners to existing selects (Pack 1)
    this.attachSelectListeners();

    // Initialize pre-selected values for Pack 1
    this.initializeSelections();

    // Attach size chart button listeners
    this.attachSizeChartListeners();

    // Sync with already-selected pack size on init
    const selectedPack = document.querySelector('.tn-bundle-pack-selector__radio:checked');
    if (selectedPack) {
      const packCount = parseInt(selectedPack.dataset.packCount, 10);
      this.updateConfigurators(packCount);
    }
  }

  attachSizeChartListeners() {
    const sizeChartButtons = this.querySelectorAll('[data-open-size-chart]');
    sizeChartButtons.forEach((button) => {
      button.addEventListener('click', () => this.openSizeChart());
    });
  }

  initializeSelections() {
    // Initialize selections for all existing packs with pre-selected values
    const packs = this.container.querySelectorAll('.tn-bundle-configurator__pack');

    packs.forEach((pack) => {
      const packIndex = parseInt(pack.dataset.packIndex, 10);

      if (!this.selections.has(packIndex)) {
        this.selections.set(packIndex, {});
      }

      const packSelections = this.selections.get(packIndex);

      // Get pre-selected color from hidden input
      const colorInput = pack.querySelector('input[data-option-type="color"]');
      if (colorInput && colorInput.value) {
        packSelections.color = colorInput.value;
      }

      // Get pre-selected size from select
      const sizeSelect = pack.querySelector('select[data-option-type="size"]');
      if (sizeSelect && sizeSelect.value) {
        packSelections.size = sizeSelect.value;
      }
    });
  }

  attachSelectListeners() {
    // Handle standard select elements (for size)
    const selects = this.container.querySelectorAll('select[data-option-type]');
    selects.forEach((select) => {
      select.addEventListener('change', this.handleVariantChange.bind(this));

      // Initialize selection if value is already selected
      if (select.value) {
        const packIndex = parseInt(select.dataset.packIndex, 10);
        const optionType = select.dataset.optionType;

        if (!this.selections.has(packIndex)) {
          this.selections.set(packIndex, {});
        }

        this.selections.get(packIndex)[optionType] = select.value;
      }
    });

    // Handle custom color dropdowns
    this.attachColorDropdownListeners();
  }

  attachColorDropdownListeners() {
    const colorDropdowns = this.container.querySelectorAll('[data-color-dropdown]');

    colorDropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('[data-color-trigger]');
      const options = dropdown.querySelectorAll('[data-color-option]');

      // Toggle dropdown on trigger click
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleColorDropdown(dropdown);
        });
      }

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectColorOption(dropdown, option);
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-color-dropdown]')) {
        this.closeAllColorDropdowns();
      }
    });
  }

  toggleColorDropdown(dropdown) {
    const isOpen = dropdown.dataset.open === 'true';

    // Close all other dropdowns first
    this.closeAllColorDropdowns();

    // Toggle this dropdown
    dropdown.dataset.open = isOpen ? 'false' : 'true';
  }

  closeAllColorDropdowns() {
    const dropdowns = this.querySelectorAll('[data-color-dropdown]');
    dropdowns.forEach((d) => {
      d.dataset.open = 'false';
    });
  }

  selectColorOption(dropdown, option) {
    const value = option.dataset.value;
    const colorHandle = option.dataset.colorHandle;
    const packIndex = parseInt(dropdown.dataset.packIndex, 10);

    // Update trigger display
    const trigger = dropdown.querySelector('[data-color-trigger]');
    const selectedSwatch = trigger.querySelector('[data-selected-swatch]');
    const selectedValue = trigger.querySelector('[data-selected-value]');

    if (selectedSwatch) {
      selectedSwatch.className = 'tn-bundle-configurator__color-swatch tn-bundle-configurator__color-swatch--' + colorHandle;
    }

    if (selectedValue) {
      selectedValue.textContent = value;
    }

    trigger.dataset.hasSelection = 'true';

    // Update hidden input
    const hiddenInput = dropdown.querySelector('input[type="hidden"]');
    if (hiddenInput) {
      hiddenInput.value = value;
      hiddenInput.dataset.packIndex = packIndex;
    }

    // Mark selected option
    dropdown.querySelectorAll('[data-color-option]').forEach((opt) => {
      opt.dataset.selected = opt === option ? 'true' : 'false';
    });

    // Close dropdown
    dropdown.dataset.open = 'false';

    // Trigger variant change
    this.handleColorChange(packIndex, value);
  }

  handleColorChange(packIndex, value) {
    // Store selection
    if (!this.selections.has(packIndex)) {
      this.selections.set(packIndex, {});
    }

    const packSelections = this.selections.get(packIndex);
    packSelections.color = value;

    // Update product images when color changes on Pack 1
    if (packIndex === 1 && value) {
      this.updateProductImages(value);
    }

    // Dispatch event for other components to listen
    this.dispatchEvent(
      new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          packIndex,
          optionType: 'color',
          value,
          selections: this.selections,
        },
      })
    );
  }

  handlePackSelected(event) {
    const { packCount } = event.detail;
    this.updateConfigurators(packCount);
  }

  updateConfigurators(count) {
    const currentConfigs = this.container.querySelectorAll('.tn-bundle-configurator__pack');
    const currentCount = currentConfigs.length;

    if (count === currentCount) {
      return;
    }

    if (count > currentCount) {
      for (let i = currentCount + 1; i <= count; i++) {
        this.addConfigurator(i);
      }
    } else {
      for (let i = currentCount; i > count; i--) {
        const config = this.container.querySelector(`[data-pack-index="${i}"]`);
        if (config) {
          this.selections.delete(i);
          config.remove();
        }
      }
    }
  }

  addConfigurator(index) {
    if (!this.template) return;

    const clone = this.template.content.cloneNode(true);
    const packElement = clone.querySelector('.tn-bundle-configurator__pack');

    // Set pack index
    packElement.dataset.packIndex = index;

    // Update pack number displays
    const numberSpan = clone.querySelector('.tn-bundle-configurator__pack-number');
    const numberText = clone.querySelector('.pack-number-text');
    if (numberSpan) numberSpan.textContent = index;
    if (numberText) numberText.textContent = index;

    // Update select IDs and names (for size dropdown)
    const selects = clone.querySelectorAll('select');
    selects.forEach((select) => {
      const optionType = select.dataset.optionType;
      select.id = `pack-${index}-${optionType}`;
      select.name = `pack-${index}-${optionType}`;
      select.dataset.packIndex = index;

      // Add change event listener
      select.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Update color dropdown pack index
    const colorDropdown = clone.querySelector('[data-color-dropdown]');
    if (colorDropdown) {
      colorDropdown.dataset.packIndex = index;

      // Update hidden input
      const hiddenInput = colorDropdown.querySelector('input[type="hidden"]');
      if (hiddenInput) {
        hiddenInput.name = `pack-${index}-color`;
        hiddenInput.dataset.packIndex = index;
      }
    }

    // Update labels
    const labels = clone.querySelectorAll('label');
    labels.forEach((label, i) => {
      const select = selects[i];
      if (select) {
        label.setAttribute('for', select.id);
      }
    });

    this.container.appendChild(clone);

    // Attach color dropdown listeners to the newly added configurator
    const newPackElement = this.container.querySelector(`[data-pack-index="${index}"]`);
    if (newPackElement) {
      const dropdown = newPackElement.querySelector('[data-color-dropdown]');
      if (dropdown) {
        const trigger = dropdown.querySelector('[data-color-trigger]');
        const options = dropdown.querySelectorAll('[data-color-option]');

        if (trigger) {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleColorDropdown(dropdown);
          });
        }

        options.forEach((option) => {
          option.addEventListener('click', (e) => {
            e.preventDefault();
            this.selectColorOption(dropdown, option);
          });
        });
      }
    }

    // Attach size chart listeners to the newly added configurator
    const sizeChartButton = this.container.querySelector(`[data-pack-index="${index}"] [data-open-size-chart]`);
    if (sizeChartButton) {
      sizeChartButton.addEventListener('click', () => this.openSizeChart());
    }
  }

  handleVariantChange(event) {
    const select = event.target;
    const packIndex = parseInt(select.dataset.packIndex, 10);
    const optionType = select.dataset.optionType;
    const value = select.value;

    // Store selection
    if (!this.selections.has(packIndex)) {
      this.selections.set(packIndex, {});
    }

    const packSelections = this.selections.get(packIndex);
    packSelections[optionType] = value;

    // Update product images when color changes on Pack 1
    if (packIndex === 1 && optionType === 'color' && value) {
      this.updateProductImages(value);
    }

    // Dispatch event for other components to listen
    this.dispatchEvent(
      new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          packIndex,
          optionType,
          value,
          selections: this.selections,
        },
      })
    );
  }

  async updateProductImages(colorValue) {
    if (!this.variants || !this.variants.length) return;

    const matchingVariant = this.variants.find((variant) => {
      return variant.option1 === colorValue || variant.option2 === colorValue || variant.option3 === colorValue;
    });

    if (!matchingVariant) return;

    let sectionId;
    const productInfoSection = document.querySelector('[id*="product-information"]');

    if (productInfoSection) {
      sectionId = productInfoSection.id.replace('shopify-section-', '');
    } else {
      const mediaGallerySection = document.querySelector('media-gallery')?.closest('.shopify-section');
      if (mediaGallerySection) {
        sectionId = mediaGallerySection.id.replace('shopify-section-', '');
      }
    }

    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', matchingVariant.id);
    url.searchParams.set('sections', sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;

      const data = await response.json();
      const html = data[sectionId];

      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newMediaGallery = doc.querySelector('media-gallery');
      const currentMediaGallery = document.querySelector('media-gallery');

      if (newMediaGallery && currentMediaGallery) {
        currentMediaGallery.replaceWith(newMediaGallery);
      }

      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('variant', matchingVariant.id);
      window.history.replaceState({}, '', newUrl.toString());

    } catch (error) {
      console.error('Error updating product images:', error);
    }
  }

  getSelections() {
    return this.selections;
  }

  validateSelections() {
    this.initializeSelections();

    const errors = [];
    const configCount = this.container.querySelectorAll('.tn-bundle-configurator__pack').length;
    const selections = [];

    for (let i = 1; i <= configCount; i++) {
      const packSelections = this.selections.get(i);

      if (!packSelections || !packSelections.color) {
        errors.push(`Pack ${i}: Please select a color`);
      }

      if (!packSelections || !packSelections.size) {
        errors.push(`Pack ${i}: Please select a size`);
      }

      if (packSelections) {
        selections.push(packSelections);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      selections,
    };
  }

  openSizeChart() {
    document.dispatchEvent(new CustomEvent('size-chart:open'));
  }
}

// Register custom elements
if (!customElements.get('bundle-pack-selector-component')) {
  customElements.define('bundle-pack-selector-component', BundlePackSelectorComponent);
}

if (!customElements.get('bundle-configurator-component')) {
  customElements.define('bundle-configurator-component', BundleConfiguratorComponent);
}

export { BundlePackSelectorComponent, BundleConfiguratorComponent };